import React from 'react'

import type { MapSourceDataEvent } from 'mapbox-gl'
import { Layer, useMap } from 'react-map-gl/mapbox'

import {
  TILESET_FILL_OUTLINE_LAYER_PREFIX,
  TILESET_LINE_LAYER_PREFIX,
  TILESET_SOURCE,
} from '../../../MapBoxViewer/mapboxConstants'
import {
  buildTilesetFeatureColor,
  TilesetFeatureColorProperty,
} from '../../../MapBoxViewer/tilesetFeatureColor'
import {
  TilesetLineStyle,
  TILESET_DRAW_ORDER,
} from '../../../MapBoxViewer/tilesetFeatureStyle'

interface IProps {
  tileId: string
  isHidden?: boolean
}

export default function GlobeViewTilesetLines({
  tileId,
  isHidden,
}: IProps): JSX.Element {
  const { current: mapRef } = useMap()
  const sourceId = TILESET_SOURCE + tileId
  const [paint, setPaint] = React.useState(() => ({
    outline: new TilesetLineStyle(0.5).paint,
    line: new TilesetLineStyle(1).paint,
  }))

  React.useEffect(() => {
    const map = mapRef?.getMap()
    if (!map) {
      return
    }
    const outline = new TilesetLineStyle(0.5)
    const line = new TilesetLineStyle(1)
    let pending = true
    const collect = () => {
      if (!pending || !map.getSource(sourceId)) {
        return
      }
      pending = false
      let changed = false
      for (const feature of map.querySourceFeatures(sourceId, {
        sourceLayer: 'features',
        filter: ['has', 'dashPattern'],
      })) {
        changed = outline.add(feature.properties) || changed
        changed = line.add(feature.properties) || changed
      }
      if (changed) {
        setPaint({ outline: outline.paint, line: line.paint })
      }
    }
    const onSourceData = (event: MapSourceDataEvent) => {
      if (event.sourceId === sourceId) {
        pending = true
      }
    }
    map.on('sourcedata', onSourceData)
    map.on('idle', collect)
    collect()
    return () => {
      map.off('sourcedata', onSourceData)
      map.off('idle', collect)
    }
  }, [mapRef, sourceId])

  const visibility = isHidden ? 'none' : 'visible'
  return (
    <>
      <Layer
        id={TILESET_FILL_OUTLINE_LAYER_PREFIX + tileId}
        type="line"
        filter={['==', ['geometry-type'], 'Polygon']}
        source={sourceId}
        source-layer="features"
        layout={{ 'line-sort-key': TILESET_DRAW_ORDER, visibility }}
        paint={{
          'line-color': buildTilesetFeatureColor(
            TilesetFeatureColorProperty.Stroke,
            '#f6ff00',
          ),
          ...paint.outline,
        }}
      />
      <Layer
        id={TILESET_LINE_LAYER_PREFIX + tileId}
        type="line"
        filter={['==', ['geometry-type'], 'LineString']}
        source={sourceId}
        source-layer="features"
        layout={{ 'line-sort-key': TILESET_DRAW_ORDER, visibility }}
        paint={{
          'line-color': buildTilesetFeatureColor(
            TilesetFeatureColorProperty.Stroke,
            '#ff7b00',
          ),
          ...paint.line,
        }}
      />
    </>
  )
}
